<template>
  <header v-if="show" class="navigation-header">
    <div class="container">
      <div class="navigation-header__buttons">
        <div
          v-for="(button, index) in navigation"
          :key="index"
          class="navigation-button-container"
          :class="{ 'navigation-button-container--active': button.active }"
        >
          <RouterLink
            :class="['navigation-button', { 'navigation-button--disabled': button.disabled }]"
            :to="{ name: button.route, params }"
            :disabled="button.disabled"
            :tabindex="button.disabled ? -1 : 0"
          >
            <div class="navigation-button__icon-container">
              <component :is="icon(button.name)" />
            </div>
            <div class="navigation-button__text-container">
              <span class="button-text">{{ $t(button.name) }}</span>
            </div>
          </RouterLink>
          <RightArrowIcon v-if="index !== navigation.length - 1" class="right-arrow-icon" />
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import EditIcon from '@/assets/icons/EditIcon.vue';
import RightArrowIcon from '@/assets/icons/RightArrowIcon.vue';
import EyeIcon from '@/assets/icons/EyeIcon.vue';
import ExportIcon from '@/assets/icons/ExportIcon.vue';
import TaskIcon from '@/assets/icons/TaskIcon.vue';
import { useRoute } from 'vue-router';
import { computed, ref, watch, reactive, onMounted, onUpdated } from 'vue';
import useExercises from '@/services/useExercises';
import { VIEWS } from '@/router';
import { taskIdDefault } from '@/types/enums/WritingStory.enum';
import useWriteStory from '@/services/useWriteStory';
const route = useRoute();
const { exists, exercise } = useExercises();
const isUnsavedExercise = ref<boolean>(true);
const items = reactive([
  {
    name: 'TASK',
    route: VIEWS.task,
    active: false,
    visible: false,
    disabled: false,
  },
  {
    name: 'WRITE',
    route: VIEWS.write,
    active: false,
    visible: true,
    disabled: false,
  },
  {
    name: 'CHECK',
    route: VIEWS.control,
    active: false,
    visible: true,
    disabled: isUnsavedExercise,
  },
  {
    name: 'SHARE',
    route: VIEWS.share,
    active: false,
    visible: true,
    disabled: isUnsavedExercise,
  },
]);

const icon = (buttonName) => {
  switch (buttonName) {
    case 'TASK':
      return TaskIcon;
    case 'WRITE':
      return EditIcon;
    case 'CHECK':
      return EyeIcon;
    case 'SHARE':
      return ExportIcon;
  }
};

const setup = async () => {
  if (route.params?.slug) {
    exists(
      route.params.slug as string,
      route.params?.taskId as string,
      parseInt(route.params?.version as string, 10),
    ).then((exist) => {
      isUnsavedExercise.value = !exist;
    });
  } else {
    isUnsavedExercise.value = true;
  }

  const taskVisable = route.params?.taskId !== taskIdDefault;

  items[0].visible = taskVisable;
  items[1].disabled = taskVisable && route.name === 'Task' && isUnsavedExercise.value;

  items.forEach((item) => (item.active = item.route === route.name));
};

useWriteStory({
  genreSlug: route.params.slug as string,
  taskId: route.params.taskId as string,
});

watch(route, setup);

watch(
  () => exercise.value?._rev,
  (rev) => {
    if (rev) {
      isUnsavedExercise.value = false;
    }
  },
);

onMounted(() => setup());

onUpdated(() => setup());

const navigation = computed(() => items.filter((item) => item.visible));
const params = computed(() => route.params);
const show = computed(() => items.some((item) => item.active));
</script>
